import type {
  UpdateContractItemMutationOptions,
  UpdateContractItemMutationVariables,
} from './types';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import ToastService from '~/services/toast.service';

import { i18n } from '~/utils';

import { queryKeysContract } from '../queryKeys';

import { updateContractItem } from './updateContractItem';

/**
 * Custom hook for updating an existing line item in a contract.
 * This is a partial update - only provided fields are updated. The change is
 * persisted immediately, which the success toast tells the user.
 *
 * @param options - Additional mutation options; callbacks run after the hook's own.
 * @returns The mutation; `isPending` stays true until the contract, its item lists and the item's detail have refetched.
 * @see https://app.dev.vestigas.com/redoc#tag/Contracts/operation/update_contract_item_contracts__contract_id__items__item_id__put
 */
export const useMutationUpdateContractItem = (
  options?: UpdateContractItemMutationOptions,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: async ({
      contractId,
      itemId,
      payload,
    }: UpdateContractItemMutationVariables) =>
      updateContractItem(contractId, itemId, payload),
    async onSettled(data, error, variables, onMutateResult, context) {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeysContract.getItems(variables.contractId, undefined),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeysContract.get(variables.contractId),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeysContract.getItem(
            variables.contractId,
            variables.itemId,
          ),
        }),
        options?.onSettled?.(data, error, variables, onMutateResult, context),
      ]);
    },
    async onSuccess(data, variables, onMutateResult, context) {
      ToastService.success(
        i18n.t(($) => $.contractItem.updateContractItem.success, {
          ns: 'data',
        }),
      );

      await options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
};
